// Generic confirmation dialog -- used for destructive actions like
// deactivating an account. Backdrop click and Escape cancel.

import { type Component, Show, createEffect, onCleanup } from 'solid-js';

type Props = {
  /** If null, the dialog is hidden. */
  when: string | null;
  title: string;
  /** Body text. Can mention the subject by name for clarity. */
  body: string;
  /** Label for the destructive action. */
  confirmLabel: string;
  /** Optional muted text below the buttons, e.g. caveats. */
  note?: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ConfirmDialog: Component<Props> = (props) => {
  createEffect(() => {
    if (!props.when) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !props.busy) {
        e.preventDefault();
        props.onCancel();
      }
    };
    window.addEventListener('keydown', handler);
    onCleanup(() => window.removeEventListener('keydown', handler));
  });

  return (
    <Show when={props.when !== null}>
      <div
        class="confirm-backdrop"
        onClick={(e) => {
          if (e.target === e.currentTarget && !props.busy) props.onCancel();
        }}
      >
        <div class="confirm-dialog" role="alertdialog" aria-modal="true">
          <h2>{props.title}</h2>
          <p>{props.body}</p>
          <Show when={props.error}>
            {(msg) => (
              <div class="form-error" role="alert">
                {msg()}
              </div>
            )}
          </Show>
          <div class="confirm-buttons">
            <button
              type="button"
              class="ghost"
              onClick={() => props.onCancel()}
              disabled={props.busy}
            >
              Cancel
            </button>
            <button
              type="button"
              class="danger"
              onClick={() => props.onConfirm()}
              disabled={props.busy}
            >
              {props.busy ? 'Working…' : props.confirmLabel}
            </button>
          </div>
          <Show when={props.note}>{(n) => <p class="confirm-note">{n()}</p>}</Show>
        </div>
      </div>
    </Show>
  );
};
